/**
 * Fetch wrapper for /v1/* routes.
 * - Same-origin in prod (behind Caddy). Dev proxied by Vite.
 * - Cookie-based session auth (HttpOnly session cookie set by backend).
 * - CSRF double-submit: backend returns csrfToken in body on /auth/me (the
 *   session itself comes from the OIDC callback's Set-Cookie). We cache it in memory and echo as X-CSRF-Token on mutations.
 * - Throws ApiError for non-2xx; returns parsed JSON otherwise.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type Json = Record<string, unknown> | unknown[] | string | number | boolean | null;

interface RequestOpts {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: Json;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null | undefined): void {
  csrfToken = token ?? null;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

export async function api<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...opts.headers,
  };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET" && csrfToken) headers["X-CSRF-Token"] = csrfToken;

  const init: RequestInit = {
    method,
    credentials: "same-origin",
    headers,
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  if (opts.signal) init.signal = opts.signal;

  const res = await fetch(path, init);

  const ct = res.headers.get("content-type") ?? "";
  const parsed: unknown =
    res.status === 204
      ? null
      : ct.includes("application/json")
        ? await res.json()
        : await res.text();

  if (!res.ok) {
    const body = parsed as { code?: string; message?: string } | string | null;
    const code =
      body && typeof body === "object" && "code" in body && body.code
        ? body.code
        : `HTTP_${res.status}`;
    const msg =
      body && typeof body === "object" && "message" in body && body.message
        ? body.message
        : res.statusText;
    throw new ApiError(res.status, code, msg, parsed);
  }

  return parsed as T;
}
